import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const SESSION_KEY = "ss_chat_session";

export const AIChat = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm Aria ✦ your beauty concierge. Ask me about services, bridal packages or academy courses!" },
  ]);
  const endRef = useRef(null);
  const sessionId = useRef(localStorage.getItem(SESSION_KEY) || `sess_${Math.random().toString(36).slice(2)}`);

  useEffect(() => { localStorage.setItem(SESSION_KEY, sessionId.current); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Please call us at +91 9916161865." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ai-chat-open"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 btn-gold rounded-full animate-float shadow-[0_0_30px_rgba(212,175,55,0.4)]">
          <Sparkles size={18} /> Ask Aria
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] glass rounded-xl flex flex-col overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.7)]" data-testid="ai-chat-window">
          <div className="flex items-center justify-between p-4 border-b border-gold/20 bg-royal/40">
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-gold" />
              <div><p className="text-sm font-medium">Aria</p><p className="text-[10px] text-white/50">Beauty Concierge</p></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-chat-close" className="text-white/60 hover:text-gold"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-lg text-sm leading-relaxed ${m.role === "user" ? "bg-gold text-midnight" : "bg-white/5 border border-gold/15 text-white/85"}`}>
                  {m.content || <span className="opacity-50">…</span>}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-gold/20 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question…" data-testid="ai-chat-input"
              className="flex-1 bg-white/5 border border-gold/20 rounded-lg px-3 py-2 text-sm outline-none focus:border-gold" />
            <button onClick={send} disabled={busy} data-testid="ai-chat-send" className="btn-gold px-3 py-2 disabled:opacity-50"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
};
